import React, { useState } from 'react';
import Layout from '../../layout';
import Container from '../../components/ui/container';
import { Input } from '../../components/ui/input';
import { Button } from '../../components/ui/button';
import { createFlashSale } from '../../lib/api/flashSaleApi';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { RiFlashlightLine, RiArrowLeftLine, RiCalendarEventLine, RiTimeLine } from 'react-icons/ri';

export default function AddFlashSale() {
    const [form, setForm] = useState({ name: '', startTime: '', endTime: '', status: 'active', metadata: '' });
    const [saving, setSaving] = useState(false);
    const navigate = useNavigate();

    const set = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

    const save = async () => {
        if (!form.name.trim()) {
            toast.error('Flash Sale Name is required');
            return;
        }
        if (!form.startTime || !form.endTime) {
            toast.error('Start Time and End Time are required');
            return;
        }

        setSaving(true);
        try {
            const convertToMySQL = (dateStr) => {
                if (!dateStr || dateStr === '') return null;
                const d = new Date(dateStr);
                if (isNaN(d.getTime())) return null;

                const pad = (n) => n.toString().padStart(2, '0');
                return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
            };

            const payload = {
                ...form,
                startTime: convertToMySQL(form.startTime),
                endTime: convertToMySQL(form.endTime)
            };

            const response = await createFlashSale(payload);
            if (response.success) {
                toast.success('Flash sale event created successfully!');
                navigate(`/flash-sale/edit/${response.saleID}`);
            } else {
                toast.error(response.message || 'Failed to create flash sale');
            }
        } catch (error) {
            console.error('Error creating flash sale:', error);
            toast.error(error?.response?.data?.message || 'Failed to create flash sale');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Layout title={'Add Flash Sale'} active={'admin-flash-sale-add'}>
            <div className="min-h-screen bg-slate-50/50 p-6 space-y-6">
                <Container>
                    {/* Header */}
                    <div className="mb-6 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4">
                        <div>
                            <button
                                onClick={() => navigate('/flash-sale/list')}
                                className="inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-amber-600 mb-2 transition-colors"
                            >
                                <RiArrowLeftLine className="w-4 h-4" /> Back to Flash Sales
                            </button>
                            <h1 className="text-2xl font-bold text-slate-900 tracking-tight flex items-center gap-2">
                                <RiFlashlightLine className="text-amber-500" /> Create Flash Sale Event
                            </h1>
                            <p className="text-slate-500 text-sm mt-0.5">
                                Set up promotional limited-time event windows and discounts.
                            </p>
                        </div>
                        <div className="flex items-center gap-3">
                            <Button
                                variant="outline"
                                onClick={() => navigate('/flash-sale/list')}
                                className="bg-white hover:bg-slate-50 text-slate-700 border-slate-200"
                            >
                                Cancel
                            </Button>
                            <Button
                                onClick={save}
                                disabled={saving}
                                className="bg-amber-600 hover:bg-amber-700 text-white font-medium shadow-sm min-w-[130px]"
                            >
                                {saving ? 'Creating...' : 'Create Event'}
                            </Button>
                        </div>
                    </div>

                    {/* Form Body Card */}
                    <div className="max-w-3xl bg-white rounded-xl p-6 border border-slate-200 shadow-sm space-y-6">
                        <h2 className="text-base font-semibold text-slate-900 pb-3 border-b border-slate-100 flex items-center gap-2">
                            <RiCalendarEventLine className="text-amber-500" /> Event Details & Schedule
                        </h2>

                        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                            <div className="md:col-span-2">
                                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                    Flash Sale Campaign Name *
                                </label>
                                <Input
                                    placeholder="e.g. Midnight Mega Rush Sale"
                                    value={form.name}
                                    onChange={e => set('name', e.target.value)}
                                    className="h-10 text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                                    <RiTimeLine className="text-slate-400" /> Start Date & Time *
                                </label>
                                <Input
                                    type="datetime-local"
                                    value={form.startTime}
                                    onChange={e => set('startTime', e.target.value)}
                                    className="h-10 text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2 flex items-center gap-1">
                                    <RiTimeLine className="text-slate-400" /> End Date & Time *
                                </label>
                                <Input
                                    type="datetime-local"
                                    value={form.endTime}
                                    onChange={e => set('endTime', e.target.value)}
                                    className="h-10 text-sm"
                                />
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                    Initial Status
                                </label>
                                <select
                                    value={form.status}
                                    onChange={e => set('status', e.target.value)}
                                    className="w-full h-10 border border-slate-200 rounded-lg px-3 text-sm bg-white text-slate-800 focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500"
                                >
                                    <option value="active">Active</option>
                                    <option value="inactive">Inactive / Draft</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-xs font-semibold text-slate-600 uppercase tracking-wider mb-2">
                                    Metadata / Config (Optional JSON)
                                </label>
                                <Input
                                    placeholder='e.g. {"bannerTag": "24h Only"}'
                                    value={form.metadata}
                                    onChange={e => set('metadata', e.target.value)}
                                    className="h-10 text-sm font-mono"
                                />
                            </div>
                        </div>
                    </div>
                </Container>
            </div>
        </Layout>
    );
}
